/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CalendarToday,
  Close,
  Info,
  KeyboardArrowDown,
  MoreVert,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import FilterCardBar from "components/FilterCardBar";
import EmployeeSearchBar from "layouts/pages/jobs/components/ShiftJobs/EmployeeSearchBar";
import { useAppContextController } from "context/AppContext";
import { useMutation, useQuery, useQueryClient } from "react-query";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import { useSnackbar } from "notistack";
import updateJob from "layouts/pages/jobs/actions/updateJob";
import { useTheme } from "@mui/material/styles";
import updateCompanyJob from "layouts/pages/companyjobs/actions/updateJob";
import moment from "moment";
import { ensureSlugs } from "utils/helpers/slugs";
import { useNavigate } from "react-router-dom";
import MDTypography from "components/MDTypography";
import fetchShiftJobPunches from "api/jobs/fetchShiftJobPunches";
import PictureAvatar from "components/PictureAvatar";
import ViewShiftsModal from "./ViewShiftsModal";
import ShiftCalendar from "../ShiftCalendarView/ShiftCalendarViewModal";
import ShiftPreviewAndEditPunchesModal from "../ShiftPreviewAndEditPunches/ShiftPreviewAndEditPunchesModal";
import PunchStatusChip from "./PunchStatusChip";

const StaffingPoolModal = ({ open, onClose, isVenue = false, initialShiftFilter = null }) => {
  // Basic state
  const [filterTab, setFilterTab] = useState("All");
  const [shiftFilter, setShiftFilter] = useState(initialShiftFilter || "All Shifts");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showShiftCalendar, setShowShiftCalendar] = useState(false);

  // Date range state
  const [dateRange, setDateRange] = useState({
    start: moment("2025-06-01"),
    end: moment("2025-06-07"),
  });

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Modal state
  const [viewShiftsModalOpen, setViewShiftsModalOpen] = useState(false);
  const [isTimecardModalOpen, setIsTimecardModalOpen] = useState(false);

  // Context & queries
  const { currentJob, setCurrentJob } = useAppContextController();

  const theme = useTheme();

  // Update shift filter when modal opens with specific shift
  useEffect(() => {
    if (open && initialShiftFilter) {
      setShiftFilter(initialShiftFilter);
    } else if (open && !initialShiftFilter) {
      setShiftFilter("All Shifts");
    }
  }, [open, initialShiftFilter]);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Staffing pool query
  const allOptions = useMemo(
    () => ({
      fetchAll: true,
      includeVerified: true,
      filters: {
        status: "Employee",
        "jobs.jobSlug": currentJob?.jobSlug,
      },
      useOrTags: true,
      useElemMatch: true,
    }),
    [currentJob?.jobSlug]
  );

  const { data: staffingPoolData, isLoading: isStaffingPoolLoading } = useQuery(
    ["shiftJobAllStaffingPool", allOptions],
    () => fetchApplicants(allOptions),
    {
      enabled: !!currentJob,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch punches for all shifts in the date range
  const shiftSlugs = useMemo(() => {
    if (!currentJob?.shifts) return [];
    return currentJob.shifts.map((shift) => shift.slug).filter(Boolean);
  }, [currentJob?.shifts]);

  const { data: allPunchesData, isLoading: isPunchesLoading } = useQuery(
    ["shiftJobPunchesForAllShifts", { jobId: currentJob?._id, shiftSlugs }],
    async () => {
      if (!currentJob?._id || !shiftSlugs.length) return [];

      // Fetch punches for each shift
      const punchesPromises = shiftSlugs.map(async (shiftSlug) => {
        try {
          const punches = await fetchShiftJobPunches({
            jobId: currentJob._id,
            shiftSlug,
          });
          return punches; // Remove the mapping since shiftSlug is already in the response
        } catch (error) {
          console.error(`Error fetching punches for shift ${shiftSlug}:`, error);
          return [];
        }
      });

      const allPunches = await Promise.all(punchesPromises);
      return allPunches.flat();
    },
    {
      enabled: !!currentJob?._id && !!shiftSlugs.length,
      refetchOnWindowFocus: false,
    }
  );

  // Initialize date range based on earliest punch date
  useEffect(() => {
    // Only set date range if it's still the default (first load)
    if (
      allPunchesData &&
      allPunchesData.length > 0 &&
      dateRange.start.isSame(moment("2025-06-01"), "day")
    ) {
      const punchDates = allPunchesData.map((punch) => moment(punch.timeIn));
      const earliestDate = moment.min(punchDates);

      if (earliestDate.isValid()) {
        const newDateRange = {
          start: earliestDate.startOf("week"),
          end: earliestDate.startOf("week").add(6, "days"),
        };
        setDateRange(newDateRange);
      }
    }
    // eslint-disable-next-line
  }, [allPunchesData]);

  // Process staff data
  const staffData = useMemo(() => {
    if (!staffingPoolData?.data || !currentJob?.shifts) {
      return [];
    }

    const { shifts } = currentJob;
    const applicantList = staffingPoolData.data;

    return applicantList.map((applicantObj) => {
      const { _id, lastName, firstName, phone, email, city, state } = applicantObj;

      // Find all shifts this applicant is assigned to
      const assignedShifts = shifts.filter((shift) =>
        (shift.shiftRoster || []).some((r) => (typeof r === "string" ? r : r._id) === _id)
      );

      // For each shift, get assigned days
      const applicantShiftDays = {};
      assignedShifts.forEach((shiftObj) => {
        const { shiftName, defaultSchedule } = shiftObj;
        if (defaultSchedule) {
          Object.entries(defaultSchedule).forEach(([dayKey, schedObj]) => {
            const { roster } = schedObj;
            if ((roster || []).some((r) => (typeof r === "string" ? r : r._id) === _id)) {
              if (!applicantShiftDays[shiftName]) applicantShiftDays[shiftName] = [];
              applicantShiftDays[shiftName].push(dayKey.substring(0, 3).toUpperCase());
            }
          });
        }
      });

      // Get punches for this applicant
      const applicantPunches = allPunchesData?.filter((punch) => punch.applicantId === _id) || [];

      // Filter punches by date range
      const filteredPunches = applicantPunches.filter((punch) => {
        const punchDate = moment(punch.timeIn);
        return punchDate.isBetween(dateRange.start, dateRange.end, "day", "[]");
      });

      // Group punches by shift
      const punchesByShift = {};
      filteredPunches.forEach((punch) => {
        const shiftName = shifts.find((s) => s.slug === punch.shiftSlug)?.shiftName;
        if (shiftName) {
          if (!punchesByShift[shiftName]) {
            punchesByShift[shiftName] = [];
          }
          punchesByShift[shiftName].push(punch);
        }
      });

      return {
        id: _id,
        lastName,
        firstName,
        phone,
        email,
        shifts: assignedShifts.map((s) => s.shiftName),
        shiftDays: applicantShiftDays,
        city,
        state,
        status: assignedShifts.length > 0 ? "scheduled" : "not-schedule",
        fullApplicant: applicantObj,
        punchesByShift,
      };
    });
  }, [staffingPoolData?.data, currentJob?.shifts, allPunchesData, dateRange]);

  // Dynamic shift options
  const dynamicShiftOptions = useMemo(() => {
    if (!currentJob?.shifts) return [];

    return currentJob.shifts.map((shift) => ({
      name: shift.shiftName,
      time:
        shift.defaultSchedule && Object.values(shift.defaultSchedule).some((d) => d.start && d.end)
          ? Object.entries(shift.defaultSchedule)
              .map(([day, d]) =>
                d.start && d.end
                  ? `${day.charAt(0).toUpperCase() + day.slice(1)}: ${d.start.substring(
                      11,
                      16
                    )}-${d.end.substring(11, 16)}`
                  : null
              )
              .filter(Boolean)
              .join(", ")
          : "",
      slug: shift.slug,
    }));
  }, [currentJob?.shifts]);

  // Filter data
  // Try this simplified filter logic to test:
  const filteredData = useMemo(() => {
    return staffData.filter((staff, index) => {
      // 1. Search filter
      const matchesSearch =
        !searchTerm ||
        staff.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.lastName?.toLowerCase().includes(searchTerm.toLowerCase());

      // 2. Status filter - simplified
      let matchesFilter = true; // Start with true

      if (filterTab === "scheduled") {
        matchesFilter = staff.shifts && Array.isArray(staff.shifts) && staff.shifts.length > 0;
      } else if (filterTab === "not-schedule") {
        matchesFilter = !staff.shifts || !Array.isArray(staff.shifts) || staff.shifts.length === 0;
      }
      // For "All", matchesFilter stays true

      // 3. Shift filter - simplified
      let matchesShift = true;
      if (shiftFilter !== "All Shifts") {
        matchesShift =
          staff.shifts && Array.isArray(staff.shifts) && staff.shifts.includes(shiftFilter);
      }

      const result = matchesSearch && matchesFilter && matchesShift;

      return result;
    });
  }, [staffData, filterTab, shiftFilter, searchTerm]);

  // Calculate counts
  const { allCount, scheduledCount, nonScheduledCount } = useMemo(() => {
    // First filter by shift if a specific shift is selected
    const shiftFilteredData =
      shiftFilter === "All Shifts"
        ? staffData
        : staffData.filter(
            (staff) =>
              staff.shifts && Array.isArray(staff.shifts) && staff.shifts.includes(shiftFilter)
          );

    return {
      allCount: shiftFilteredData.length,
      scheduledCount: shiftFilteredData.filter((s) => s.shifts.length > 0).length,
      nonScheduledCount: shiftFilteredData.filter((s) => s.shifts.length === 0).length,
    };
  }, [staffData, shiftFilter]);

  // Dynamic filter cards based on whether a specific shift is selected
  const filterCards = useMemo(() => {
    if (initialShiftFilter) {
      // When a specific shift is selected, show only "All" filter
      return [
        {
          icon: <Info />,
          title: "All",
          color: "primary",
          number: allCount,
          filterField: "status",
          filterValue: "All",
        },
      ];
    }

    // When no specific shift is selected, show all filters
    return [
      {
        icon: <Info />,
        title: "All",
        color: "primary",
        number: allCount,
        filterField: "status",
        filterValue: "All",
      },
      {
        icon: <Info />,
        title: "Scheduled",
        color: "success",
        number: scheduledCount,
        filterField: "status",
        filterValue: "scheduled",
      },
      {
        icon: <Info />,
        title: "Not Scheduled",
        color: "secondary",
        number: nonScheduledCount,
        filterField: "status",
        filterValue: "not-schedule",
      },
    ];
  }, [allCount, scheduledCount, nonScheduledCount, initialShiftFilter]);

  // Utility functions
  const getAvailableDaysForShift = useCallback(
    (shiftName) => {
      const shift = (currentJob?.shifts || []).find((s) => s.shiftName === shiftName);
      if (!shift || !shift.defaultSchedule) return [];
      return Object.entries(shift.defaultSchedule)
        .filter(([, sched]) => sched.start && sched.end)
        .map(([day]) => day.substring(0, 3).toUpperCase());
    },
    [currentJob?.shifts]
  );

  const getEmployeeShifts = useCallback(
    (employee) => {
      if (!employee || !currentJob?.shifts) return [];

      const employeeId = employee.id;
      return currentJob.shifts.filter((shift) => {
        return (shift.shiftRoster || []).some((r) => {
          const rosterId = typeof r === "string" ? r : r._id;
          return rosterId === employeeId;
        });
      });
    },
    [currentJob?.shifts]
  );

  // Event handlers
  const handleMenuClick = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleShiftCalendarClose = () => {
    setShowShiftCalendar(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedEmployee immediately - let the modal handlers do it
    // setSelectedEmployee(null);
  };

  const handleViewShifts = () => {
    if (!selectedEmployee) {
      enqueueSnackbar("No employee selected!", { variant: "error" });
      return;
    }

    // Open modal first, then close menu
    setViewShiftsModalOpen(true);

    // Close menu after a small delay to ensure modal opens
    setTimeout(() => {
      handleMenuClose();
    }, 0);
  };

  const handleShiftCalendarOpen = (employee) => {
    setSelectedEmployee(employee);
    // onClose();
    setShowShiftCalendar(true);
  };

  useEffect(() => {
    setPage(0);
  }, [filterTab, shiftFilter]);

  // And keep your existing FilterCardBar but remove the setPage logic from handleFilterChange:
  const handleFilterChange = useCallback((filter) => {
    let filterValue = filter;

    if (typeof filter === "object" && filter !== null) {
      if (filter.filterValue) {
        filterValue = filter.filterValue;
      } else if (filter.value) {
        filterValue = filter.value;
      } else if (filter.title) {
        filterValue = filter.title;
      } else {
        const entries = Object.entries(filter);
        if (entries.length > 0) {
          const [, value] = entries[0];
          filterValue = value;
        }
      }
    }

    setFilterTab(filterValue);
    // Don't call setPage here - let useEffect handle it
  }, []);

  // Mutation setup
  const idName = isVenue ? "_id" : "jobId";
  const { mutateAsync: updateJobMutation } = useMutation(isVenue ? updateJob : updateCompanyJob);

  const handleUpdateJob = (updatedValues, isScheduleUpdate = false) => {
    const shiftsWithSlugs = ensureSlugs(updatedValues.shifts || [], "shiftName");
    const finalValues = {
      ...updatedValues,
      shifts: shiftsWithSlugs,
    };

    updateJobMutation(
      { [idName]: currentJob?._id, data: finalValues },
      {
        onSuccess: async (res) => {
          await queryClient.invalidateQueries("company_jobs_table");
          await queryClient.invalidateQueries("jobs");
          setCurrentJob({ ...currentJob, ...finalValues });
          if (isScheduleUpdate) {
            enqueueSnackbar("Shift schedules updated successfully.", { variant: "success" });
          } else {
            enqueueSnackbar("Job has been updated.", { variant: "success" });
          }
        },
        onError: (err) => console.log(err),
      }
    );
  };
  // Save shifts function
  const handleSaveShifts = async (shiftsData) => {
    if (!selectedEmployee?.id || !shiftsData) return;

    const applicantId = selectedEmployee.id;
    const { currentShifts, shiftDays } = shiftsData;

    // Update shifts in currentJob
    const updatedShifts = (currentJob.shifts || []).map((shift) => {
      const isChecked = currentShifts[shift.shiftName];
      const newShift = JSON.parse(JSON.stringify(shift));

      if (isChecked) {
        // Add applicant to shiftRoster if not present
        if (!newShift.shiftRoster) newShift.shiftRoster = [];

        // Check if applicant is already in shiftRoster
        const existingRosterEntry = newShift.shiftRoster.find(
          (r) => (typeof r === "string" ? r : r._id) === applicantId
        );

        if (!existingRosterEntry) {
          // Add full applicant object to shiftRoster
          const applicantData = selectedEmployee.fullApplicant;
          newShift.shiftRoster.push({
            _id: applicantId,
            fullName: `${applicantData.firstName} ${applicantData.lastName}`,
            firstName: applicantData.firstName,
            lastName: applicantData.lastName,
            email: applicantData.email,
            payRate: applicantData.payRate || "0.00",
            billRate: applicantData.billRate || "0.00",
          });
        }

        // For each selected day, add applicant ID to defaultSchedule[day].roster
        const days = shiftDays[shift.shiftName] || [];
        Object.entries(newShift.defaultSchedule || {}).forEach(([day, sched]) => {
          if (!newShift.defaultSchedule[day].roster) newShift.defaultSchedule[day].roster = [];

          if (days.includes(day.substring(0, 3).toUpperCase())) {
            // Add applicant ID to roster if not present
            if (!newShift.defaultSchedule[day].roster.includes(applicantId)) {
              newShift.defaultSchedule[day].roster.push(applicantId);
            }
          } else {
            // Remove applicant ID from days not selected
            newShift.defaultSchedule[day].roster = newShift.defaultSchedule[day].roster.filter(
              (id) => id !== applicantId
            );
          }
        });
      } else {
        // Remove applicant from shiftRoster and all days
        if (newShift.shiftRoster) {
          newShift.shiftRoster = newShift.shiftRoster.filter(
            (r) => (typeof r === "string" ? r : r._id) !== applicantId
          );
        }

        // Remove applicant ID from all day rosters
        Object.entries(newShift.defaultSchedule || {}).forEach(([day, sched]) => {
          if (newShift.defaultSchedule[day].roster) {
            newShift.defaultSchedule[day].roster = newShift.defaultSchedule[day].roster.filter(
              (id) => id !== applicantId
            );
          }
        });
      }
      return newShift;
    });

    // Create updated job object
    const updatedJob = { ...currentJob, shifts: updatedShifts };

    try {
      await updateJobMutation({ [idName]: currentJob?._id, data: updatedJob });
      setViewShiftsModalOpen(false);
      // Update the current job in context
      setCurrentJob({ _id: currentJob?._id, ...updatedJob });

      // Invalidate relevant queries
      await queryClient.invalidateQueries("company_jobs_table");
      await queryClient.invalidateQueries("shiftJobAllStaffingPool");
      await queryClient.invalidateQueries("jobs");

      enqueueSnackbar("Shifts updated successfully!", { variant: "success" });
    } catch (error) {
      console.error("Error updating shifts:", error);
      enqueueSnackbar("Failed to update shifts.", { variant: "error" });
    }
  };

  const handleViewShiftsClose = () => {
    setViewShiftsModalOpen(false);
    setSelectedEmployee(null); // Clear here instead
  };

  const handleTimecardClose = () => {
    setIsTimecardModalOpen(false);
    setSelectedEmployee(null);
  };

  // Date navigation handlers
  const handlePrevWeek = () => {
    setDateRange((prev) => {
      const newStart = moment(prev.start).subtract(7, "days");
      return {
        start: newStart,
        end: moment(newStart).add(6, "days"),
      };
    });
  };
  const handleNextWeek = () => {
    setDateRange((prev) => {
      const newStart = moment(prev.start).add(7, "days");
      return {
        start: newStart,
        end: moment(newStart).add(6, "days"),
      };
    });
  };

  // Loading state
  if (isStaffingPoolLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading staffing pool data...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentJob?.shifts) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 2, textAlign: "center" }}>
          {/* Header */}
          <Box
            sx={{
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "white",
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              STAFFING POOL
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
            No shifts available. Click the + button to add a new shift.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
                minWidth: 100,
                textTransform: "uppercase",
              }}
            >
              CLOSE
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xxl"
        fullWidth
        PaperProps={{
          sx: {
            height: "95vh",
            maxHeight: "95vh",
            borderRadius: 2,
            minWidth: "1400px",
            m: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "white",
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                STAFFING POOL {initialShiftFilter ? "For " : ""}{" "}
                <span style={{ color: theme.palette.primary.main }}>
                  {initialShiftFilter ? `${initialShiftFilter}` : ""}
                </span>
              </Typography>
              <IconButton onClick={onClose} sx={{ color: "grey.400" }}>
                <Close />
              </IconButton>
            </Box>

            {/* Controls */}
            <Box sx={{ p: 3, bgcolor: "white" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                {/* Left side - Filter Tabs and Date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Filter Tabs */}
                  <FilterCardBar
                    color="error"
                    cards={filterCards}
                    parent="Applicants"
                    bgColor="primary"
                    setFilters={handleFilterChange}
                    setPage={setPage}
                    initialIndex={0}
                  />

                  {/* Date Range */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
                    <IconButton size="small" sx={{ color: "grey.600" }} onClick={handlePrevWeek}>
                      <NavigateBefore />
                    </IconButton>
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      sx={{ color: "grey.800", fontSize: "1.1rem" }}
                    >
                      {dateRange.start.format("MMM DD")} - {dateRange.end.format("MMM DD, YYYY")}
                    </Typography>
                    <IconButton size="small" sx={{ color: "grey.600" }} onClick={handleNextWeek}>
                      <NavigateNext />
                    </IconButton>
                  </Box>
                </Box>

                {/* Right side - Shift Filter and Search */}
                <Box sx={{ display: "flex", flex: 1, gap: 2 }}>
                  {/* Shift Filter */}
                  {initialShiftFilter ? null : (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={shiftFilter}
                        onChange={(e) => setShiftFilter(e.target.value)}
                        sx={{ borderRadius: 1, height: "50px", fontSize: "14px" }}
                        disabled={!!initialShiftFilter}
                        IconComponent={KeyboardArrowDown}
                      >
                        <MenuItem value="All Shifts">All Shifts</MenuItem>
                        {dynamicShiftOptions.map((shift) => (
                          <MenuItem key={shift.name} value={shift.name}>
                            {shift.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Search */}
                  {initialShiftFilter || shiftFilter !== "All Shifts" ? null : (
                    <Box sx={{ position: "relative", maxWidth: 270 }}>
                      <EmployeeSearchBar dropRight={false} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Main Table */}
            <Box sx={{ flex: 1, bgcolor: "white", position: "relative", px: 3 }}>
              <TableContainer>
                <Table stickyHeader sx={{ tableLayout: "fixed", overflow: "hidden" }}>
                  <TableBody>
                    {/* Header Row */}
                    <TableRow
                      sx={{
                        "& .MuiTableCell-root": {
                          bgcolor: "#f8f9fa",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          fontSize: 12,
                          letterSpacing: 1,
                          position: "sticky",
                          top: 0,
                        },
                      }}
                    >
                      <TableCell sx={{ width: 80, textAlign: "center" }}>AVATAR</TableCell>
                      <TableCell sx={{ width: 130 }}>
                        LAST
                        <br />
                        NAME
                      </TableCell>
                      <TableCell sx={{ width: 130 }}>
                        FIRST
                        <br />
                        NAME
                      </TableCell>
                      <TableCell sx={{ width: 130 }}>PHONE</TableCell>
                      <TableCell sx={{ width: 200 }}>EMAIL</TableCell>
                      <TableCell sx={{ width: 200 }}>
                        SHIFT(S)
                        <br />
                        ASSIGNED
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", width: 150 }}>ACTION</TableCell>
                    </TableRow>

                    {/* Data Rows */}
                    {filteredData.length > 0 ? (
                      (() => {
                        const slicedData = filteredData.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        );

                        return slicedData.map((staff, index) => {
                          return (
                            <TableRow
                              key={`staff-${staff.id}`}
                              hover
                              sx={{
                                "&:hover": { bgcolor: "#f5f5f5" },
                                bgcolor: "white",
                              }}
                            >
                              <TableCell sx={{ py: 2, textAlign: "center" }}>
                                <PictureAvatar
                                  image={staff.fullApplicant?.profileImg}
                                  firstName={staff.fullApplicant?.firstName}
                                  lastName={staff.fullApplicant?.lastName}
                                  userId={staff.id}
                                  size="sm"
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: "14px" }}>
                                {staff.lastName}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: "14px" }}>
                                {staff.firstName}
                              </TableCell>
                              <TableCell sx={{ py: 2, fontSize: "14px" }}>{staff.phone}</TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: "14px",
                                  maxWidth: 170,
                                  width: 170,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {staff.email}
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Box>
                                  {staff.shifts.length > 0 ? (
                                    staff.shifts.map((shift) => (
                                      <Box key={`${staff.id}-${shift}`} sx={{ mb: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              mb: 0.5,
                                              lineHeight: 1.2,
                                              fontSize: 14,
                                              fontWeight: "600",
                                              color: "#1976d2",
                                            }}
                                          >
                                            {shift}
                                          </Typography>
                                          <PunchStatusChip
                                            punches={staff.punchesByShift[shift] || []}
                                            shiftName={shift}
                                          />
                                        </Box>
                                      </Box>
                                    ))
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mb: 0.5,
                                        lineHeight: 1.2,
                                        fontSize: 14,
                                        color: "grey.500",
                                      }}
                                    >
                                      Assign Shifts
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 2, textAlign: "center" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: "#e8f5e8",
                                      color: "#2e7d32",
                                      "&:hover": { bgcolor: "#c8e6c9" },
                                    }}
                                    onClick={() => handleShiftCalendarOpen(staff)}
                                  >
                                    <CalendarToday sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: "#e3f2fd",
                                      color: "#1976d2",
                                      "&:hover": { bgcolor: "#bbdefb" },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/employees/${staff.id}/action/employeeinfo`);
                                    }}
                                  >
                                    <Info sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    onClick={(e) => handleMenuClick(e, staff)}
                                    size="small"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      color: "text.secondary",
                                      "&:hover": { bgcolor: "#f5f5f5" },
                                    }}
                                  >
                                    <MoreVert sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No staff found matching the current filters.
                            <br />
                            <small>
                              Filter: {filterTab} | Shift: {shiftFilter} | Search: {searchTerm}
                            </small>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination */}
            <Box sx={{ borderTop: 1, borderColor: "divider", bgcolor: "white" }}>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                p: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: "#d32f2f",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#b71c1c",
                  },
                  minWidth: 100,
                  textTransform: "uppercase",
                }}
              >
                CLOSE
              </Button>
            </Box>

            {/* Footer */}
          </Box>

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 160 } }}
          >
            <MenuItem onClick={handleViewShifts}>
              <MDTypography variant="body2">View Shifts</MDTypography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (!selectedEmployee) {
                  enqueueSnackbar("No employee selected!", { variant: "error" });
                  return;
                }

                // Check if employee has any shifts assigned
                const employeeShifts = currentJob?.shifts?.filter((shiftItem) => {
                  return (shiftItem.shiftRoster || []).some(
                    (rosterMember) => rosterMember._id === selectedEmployee.id
                  );
                });

                if (!employeeShifts || employeeShifts.length === 0) {
                  enqueueSnackbar(
                    `${selectedEmployee.firstName} ${selectedEmployee.lastName} is not assigned to any shifts.`,
                    { variant: "error" }
                  );
                  handleMenuClose();
                  return;
                }

                setIsTimecardModalOpen(true);
                setTimeout(() => {
                  handleMenuClose();
                }, 0);
              }}
            >
              <MDTypography variant="body2">View Timecard</MDTypography>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <MDTypography variant="body2">View Notes</MDTypography>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <MDTypography variant="body2" color="grey.100">
                PTO Schedule
              </MDTypography>
            </MenuItem>
          </Menu>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      <ViewShiftsModal
        open={viewShiftsModalOpen}
        onClose={handleViewShiftsClose}
        employee={selectedEmployee}
        currentJob={currentJob}
        dynamicShiftOptions={dynamicShiftOptions}
        getAvailableDaysForShift={getAvailableDaysForShift}
        onSave={handleSaveShifts}
      />

      {selectedEmployee && (
        <ShiftCalendar
          isModalOpen={showShiftCalendar}
          setIsModalOpen={handleShiftCalendarClose}
          employees={[selectedEmployee?.fullApplicant]}
          job={currentJob}
          onUpdate={handleUpdateJob}
          shifts={currentJob?.shifts}
        />
      )}

      {selectedEmployee && isTimecardModalOpen && (
        <ShiftPreviewAndEditPunchesModal
          shift={currentJob?.shifts?.[0] || {}} // Default to first shift, will be filtered by employee
          isShiftEditPunchesModalOpen={isTimecardModalOpen}
          setShiftEditPunchesModalOpen={handleTimecardClose}
          filterByEmployee={selectedEmployee} // Pass the selected employee for filtering
        />
      )}
    </>
  );
};

export default StaffingPoolModal;
